import React, {useContext, useMemo, useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheckDouble, faHistory} from "@fortawesome/free-solid-svg-icons";

// Utils
import {getComponents, getFields} from "../../utils/builder.js";
import {exists, prettyDateInUserTimezone} from "../../utils/helpers.js";

// Contexts
import {FacilityNavContext} from "../../contexts/facilitynav.js";
import {SettingsContext} from "../../contexts/settings.js";

// Components
import Modal from "../../components/Modal.js";
import ModalElementDisabledHistory from "../checksheet-builder/ModalElementDisabledHistory.js";
import ComponentServiceStateHistory from "../../components/ComponentServiceStateHistory.js";
import Help from "../../components/Help.js";

// Style
import {voice} from "../../style/components/typography.js";
import {pad} from "../../style/components/variables.js";
import {Heading, HeadingMedium, Inline, Span, Text} from "../../style/components/general.js";

const ModalFieldRestrictions = ({visible, setVisible, checksheet}) => {
  const {name, builder, id: checksheetId} = checksheet;

  const [historyTarget, setHistoryTarget] = useState();

  const {facility} = useContext(FacilityNavContext);
  const {slug: facilitySlug} = facility || {};
  const {settings} = useContext(SettingsContext);

  const {allIds, byId} = useMemo(() => {
    const {allIds: taskAllIds, byId: taskById} = builder;
    const {byId: facilityById} = facility?.builder || {};

    const consolidated = {allIds: taskAllIds, byId: {}};
    Object.values(taskById).map(attributes => {
      const {name: elementName, help, condition, children, deleted} = attributes;
      // Consolidate task/facility attributes for groups/components
      if (facility && elementName in facilityById) {
        const taskHelp = help || [];
        const facilityHelp = facilityById[elementName]?.help || [];
        const facilityDeleted = facilityById[elementName]?.deleted;
        const facilityCondition = facilityById[elementName]?.condition;
        const consolidatedHelp = [...facilityHelp, ...taskHelp];
        const consolidatedElement = {
          ...facilityById[elementName],
          condition: facilityCondition || condition,
          children,
          deleted: facilityDeleted || deleted
        };

        if (consolidatedHelp?.length > 0) consolidatedElement.help = consolidatedHelp;

        consolidated.byId[elementName] = consolidatedElement;
      } else consolidated.byId[elementName] = attributes;
    });

    return consolidated;
  }, [facility, builder]);

  // Open single file preview
  const fields = useMemo(() => getFields(allIds, byId, ["number", "generated"]), [allIds, byId]);
  const components = useMemo(() => getComponents(allIds, byId), [allIds, byId]);

  const formatRange = (min, max, strictMin = true, strictMax = true) => {
    if (min !== null && max !== null)
      return `value ${strictMin ? ">" : ">="} ${min} and value ${strictMax ? "<" : "<="}  ${max}`;
    if (min !== null) return `value ${strictMin ? ">" : ">="} ${min}`;
    return `value ${strictMax ? "<" : "<="} ${max}`;
  };

  const formatCondition = condition => {
    let formattedCondition;
    if (condition) {
      formattedCondition = `${condition.action} when `;
      condition.list.map(({check, compare, depends, operator, trigger}, idx) => {
        if (condition.list.length > 1 && idx === condition.list.length - 1)
          formattedCondition += `, ${condition.operator} `;
        else if (condition.list.length > 1 && idx > 0) formattedCondition += ", ";

        const checkArray = Array.isArray(check) ? check : [check];
        const compareArray = Array.isArray(compare) ? compare : [compare];

        const dependsType = byId[depends]?.type || depends;
        let dependsUnits = byId[depends]?.units ? ` ${byId[depends]?.units}` : "";
        let dependsLabel = byId[depends]?.label || depends?.toUpperCase();

        if (depends === "cumulative") dependsLabel = "CUMULATIVE RAINFALL";
        if (depends === "precipitationChance") dependsLabel = "PRECIPITATION CHANCE";
        if (depends === "cumulative" || depends === "rainfall") dependsUnits = " inches";
        if (depends === "precipitationChance") dependsUnits = "%";

        formattedCondition += `${dependsLabel} `;

        if (depends === "weekday") {
          formattedCondition += "is ";
          compareArray.map((compareVal, compareIdx) => {
            formattedCondition += compareVal;
            if (compareIdx < compareArray.length - 2) formattedCondition += ", ";
            if (compareIdx === compareArray.length - 2) formattedCondition += ` ${operator} `;
          });
        } else
          checkArray.map((checkVal, checkIdx) => {
            let comp = compareArray[checkIdx];

            // Component Service State condition trigger
            if (trigger === "parentDisabled") {
              comp =
                comp === "disabled"
                  ? byId[depends]?.disabledLabel || "Out of Service"
                  : byId[depends]?.enabledLabel || "In Service";
            }

            let ch = checkVal || "is";

            if (ch === "===") ch = "is";
            if (ch === "!==") ch = "is not";
            if (ch === ">=") ch = "≥";
            if (ch === "<=") ch = "≤";
            if (ch === ">" && dependsType === "time") ch = "is after";
            if (ch === "<" && dependsType === "time") ch = "is before";
            if (dependsType === "multiselect") {
              ch = "has";
              if (comp === "checked") comp = "all checked";
              if (comp === "unchecked") comp = "all unchecked";
              if (comp === "one") comp = "one checked";
              if (comp === "more") comp = "at least one checked";
            }

            if (comp.match(/^Other: /)) comp = comp.replace(/^Other: /, "");

            formattedCondition += `${ch} ${comp}${dependsUnits}`;
            if (checkIdx < checkArray.length - 1) formattedCondition += ` ${operator} `;
          });
      });
    }

    return formattedCondition;
  };

  const readableFormulaString = formula => {
    let f = "";
    formula.map(part => {
      if (byId && part.value in byId)
        f += ` ${byId[part.value].label} ${part?.previous ? " PREVIOUS" : ""}`;
      else f += ` ${part.label}`;
    });
    return f;
  };

  const targetFields = useMemo(
    () =>
      fields?.filter(
        ({hasSetPoint, hasRange, hasARange, limits, condition, formula}) =>
          hasSetPoint ||
          hasRange ||
          hasARange ||
          (limits?.length > 0 && limits[0]?.label) ||
          condition?.list?.length ||
          formula?.length
      ),
    [fields]
  );

  const targetComponents = useMemo(
    () => components?.filter(({allowDisable}) => allowDisable),
    [components]
  );

  if (historyTarget)
    return (
      <ModalElementDisabledHistory
        visible={!!historyTarget}
        setVisible={setVisible}
        goBack={() => setHistoryTarget(undefined)}
        target={historyTarget}
        checksheetId={checksheetId}
        facilitySlug={facilitySlug}
        allowStateChange={false}
      />
    );

  return (
    <Modal visible={visible} setVisible={setVisible}>
      <Heading>{name.toUpperCase()} CHECKSHEET INFO</Heading>
      <br />
      <HeadingMedium>
        Field Restrictions&nbsp;
        <Help>
          Outlined below are any field restrictions (including conditionals, generated formulas,
          ranges, limits, or set points) configured on this checksheet. Icons next to a field name
          show that field&apos;s condition or formula.
        </Help>
      </HeadingMedium>
      {targetFields?.length > 0 ? (
        targetFields.map(
          ({
            name: fieldName,
            ancestry,
            condition,
            formula,
            hasSetPoint,
            setHigh,
            setLow,
            hasRange,
            min,
            max,
            hasARange,
            aMin,
            aMax,
            strictMin,
            strictMax,
            limits
          }) => (
            <Field key={fieldName}>
              <Inline>
                <FieldText bold>{ancestry}</FieldText>
                {condition && (
                  <Help icon={<FontAwesomeIcon icon={faCheckDouble} />}>
                    {formatCondition(condition)}
                  </Help>
                )}
                {formula?.length > 0 && (
                  <Help icon={<Formula>f(x)</Formula>}>{readableFormulaString(formula)}</Help>
                )}
              </Inline>
              {hasSetPoint && (
                <Tab>
                  Set Points: {formatRange(setLow, setHigh)}&nbsp;
                  <Help>Informational range, does not effect values.</Help>
                </Tab>
              )}
              {hasRange && (
                <Tab>
                  Restricted Range: {formatRange(min, max)}&nbsp;
                  <Help>Will not allow values outside this range to be saved.</Help>
                </Tab>
              )}
              {hasARange && (
                <Tab>
                  Acceptable Range: {formatRange(aMin, aMax, strictMin, strictMax)}&nbsp;
                  <Help>
                    Values will be saved, but an Unacceptable Parameter Notification will be sent
                  </Help>
                </Tab>
              )}
              {limits?.length > 0 &&
                limits[0]?.label &&
                limits.map(limit => (
                  <Tab key={`${limit.label}:${limit.value}`}>
                    {limit.label} Limit: {limit.value}
                  </Tab>
                ))}
            </Field>
          )
        )
      ) : (
        <Text>
          This checksheet does not include conditional fields, generated formula fields or fields
          with any restrictions (ranges, limits or set points).
        </Text>
      )}
      <br />
      <HeadingMedium>
        Component States&nbsp;
        <Help>
          Outlined below are any components configured to allow an enabled or disabled state (In
          Service / Out of Service, or custom labels). Each entry shows the current state, when it
          last changed, and recent history. The history icon opens the full state history.
        </Help>
      </HeadingMedium>
      {targetComponents?.length > 0 ? (
        <ComponentList column fullWidth>
          {targetComponents.map(
            ({name: componentName, ancestry, state, disabledLabel, enabledLabel, stateHistory}) => {
              const {disabled, time} = state || {disabled: false};
              const component = byId[componentName];

              return (
                <ComponentEntry key={componentName}>
                  <Inline wrap>
                    <Span>
                      <ComponentText bold>{ancestry}</ComponentText>
                      {" - "}
                      <StateText disabled={disabled}>
                        {disabled
                          ? disabledLabel || "Out of Service"
                          : enabledLabel || "In Service"}
                      </StateText>
                      {exists(time) && (
                        <>
                          {" since "}
                          {prettyDateInUserTimezone(time, settings?.timezone, "MMM D YYYY, h:mm A")}
                        </>
                      )}
                    </Span>
                    {stateHistory?.length > 0 && (
                      <HistoryButton
                        type="button"
                        title="Full Component Service State history"
                        onClick={() => {
                          setHistoryTarget(component);
                        }}>
                        <FontAwesomeIcon icon={faHistory} />
                      </HistoryButton>
                    )}
                  </Inline>
                  {stateHistory?.length > 0 && (
                    <ComponentServiceStateHistory
                      stateHistory={stateHistory}
                      enabledLabel={enabledLabel}
                      disabledLabel={disabledLabel}
                      facilitySlug={facilitySlug}
                      checksheetId={checksheetId}
                      timezone={settings?.timezone}
                    />
                  )}
                </ComponentEntry>
              );
            }
          )}
        </ComponentList>
      ) : (
        <Text>This checksheet does not include components that allow disabled state change.</Text>
      )}
    </Modal>
  );
};

ModalFieldRestrictions.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  checksheet: PropTypes.objectOf(PropTypes.any).isRequired
};

// Style Overrides
const Field = styled.div`
  padding: ${pad}px;
`;

const ComponentList = styled(Inline)`
  padding: ${pad}px;
  align-items: stretch;
`;

const ComponentEntry = styled.div`
  width: 100%;
`;

const Tab = styled(Text)`
  margin-left: ${pad}px;
`;

const Formula = styled.span`
  ${voice.quiet};
  font-weight: bold;
  padding: ${pad / 2}px;
  color: ${({theme}) => theme.secondary};
`;

const HistoryButton = styled.button`
  padding: 0 ${pad}px;
  width: min-content;

  svg {
    fill: ${({theme}) => theme.secondary};
  }
`;

const FieldText = styled.span`
  color: ${({theme}) => theme.field};
  font-weight: bold;
`;

const ComponentText = styled.span`
  color: ${({theme}) => theme.component};
  font-weight: bold;
`;

const StateText = styled.span`
  color: ${({theme, disabled}) => (disabled ? theme.error : theme.success)};
  font-weight: bold;
`;

export default ModalFieldRestrictions;
